import clsx from 'clsx'
import { AlertCircle, Clock, ExternalLink, Loader2, QrCode, RefreshCw, Smartphone } from 'lucide-react'
import { useEffect, useMemo } from 'react'
import { UAParser } from 'ua-parser-js'

import {
  initialState,
  useQrcodeActions,
  useQRCodeState,
  useQrcodeStore
} from './stores/qrcodeStore'

import { isANDROID, isDingTalkEnvironment, isIOS } from '@/utils/dingtalk'

const QRCodeLogin: React.FC = () => {
  const { loading, generating, qrImage, error, timeLeft, errorMessage } =
    useQrcodeStore()
  const { initialize, generateQRImage, getQRCode, clearTimers } = useQrcodeActions()
  const { QRValue } = useQRCodeState()

  // 检测是否为移动端
  const isMobile = useMemo(() => {
    // 钉钉环境下的移动端检测
    if (isDingTalkEnvironment()) {
      return isIOS() || isANDROID()
    }
    
    // 普通浏览器使用 ua-parser-js 进行检测
    if (typeof window !== 'undefined') {
      const parser = new UAParser()
      const device = parser.getDevice()
      const os = parser.getOS()
      
      // 检查设备类型
      const isMobileDevice = device.type === 'mobile' || device.type === 'tablet'
      
      // 检查操作系统
      const isMobileOS = ['Android', 'iOS'].includes(os.name || '')
      
      // 检查屏幕尺寸（作为补充判断）
      const isMobileScreen = window.innerWidth <= 768
      
      return isMobileDevice || isMobileOS || isMobileScreen
    }
    
    return false
  }, [])

  const doLogin = () => {
    if (isMobile) {
      // 移动端直接跳转
      window.location.href = QRValue
    } else {
      // 桌面端新窗口打开
      window.open(QRValue, '_blank')
    }
  }

  const handleRetry = async () => {
    clearTimers()
    initialize(initialState)
    await getQRCode()
  }

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds
      .toString()
      .padStart(2, '0')}`
  }

  // 处理qrCodeData变化，生成二维码图片
  useEffect(() => {
    if (QRValue) {
      generateQRImage()
    }
  }, [QRValue, generateQRImage])

  useEffect(() => {
    initialize(initialState)
    setTimeout(async () => {
      await getQRCode()
    }, 1000)

    return () => {
      clearTimers()
    }
  }, [initialize, getQRCode, clearTimers])

  // 渲染二维码区域
  const renderQRCodeArea = () => {
    // 加载状态
    if (loading && !error && QRValue === '') {
      return (
        <div className={clsx(
          'flex', 'flex-col', 'items-center', 'justify-center',
          'w-full', 'max-w-xs', 'h-80', 'mx-auto',
          'bg-gray-50', 'border-2', 'border-dashed', 'border-gray-200',
          'rounded-xl', 'space-y-4'
        )}>
          <div className={clsx(
            'w-12', 'h-12', 'bg-blue-100', 'rounded-full',
            'flex', 'items-center', 'justify-center'
          )}>
            <Loader2 className={clsx('w-6', 'h-6', 'text-blue-600', 'animate-spin')} />
          </div>
          <div className={clsx('text-gray-600', 'text-sm', 'text-center')}>
            {generating ? '正在生成二维码...' : '生成二维码中...'}
          </div>
        </div>
      )
    }

    // 错误状态
    if (error && (QRValue === '' || qrImage === '')) {
      return (
        <div className={clsx(
          'flex', 'flex-col', 'items-center', 'justify-center',
          'w-full', 'max-w-xs', 'h-80', 'mx-auto',
          'bg-red-50', 'border-2', 'border-dashed', 'border-red-200',
          'rounded-xl', 'space-y-4'
        )}>
          <div className={clsx(
            'w-12', 'h-12', 'bg-red-100', 'rounded-full',
            'flex', 'items-center', 'justify-center'
          )}>
            <AlertCircle className={clsx('w-6', 'h-6', 'text-red-600')} />
          </div>
          <div className={clsx('text-center', 'space-y-3')}>
            <div className={clsx('text-gray-800', 'font-medium')}>获取二维码失败</div>
            {errorMessage && (
              <div className={clsx('text-sm', 'text-red-600', 'px-4')}>
                {errorMessage}
              </div>
            )}
            <button
              onClick={handleRetry}
              disabled={loading}
              className={clsx(
                'px-4', 'py-2', 'bg-blue-600', 'hover:bg-blue-700',
                'disabled:bg-blue-400', 'disabled:cursor-not-allowed',
                'text-white', 'rounded-lg', 'font-medium',
                'flex', 'items-center', 'space-x-2',
                'transition-colors', 'duration-200'
              )}
            >
              <RefreshCw className={clsx('w-4', 'h-4', loading && 'animate-spin')} />
              <span>{loading ? '重试中...' : '重试'}</span>
            </button>
          </div>
        </div>
      )
    }

    // 成功状态 - 根据设备类型显示不同内容
    if (QRValue !== '' && (!isMobile || qrImage !== '')) {
      // 移动端显示按钮模式
      if (isMobile) {
        return (
          <div className={clsx('space-y-8')}>
            {/* 移动端图标 */}
            <div className={clsx('text-center')}>
              <div className={clsx(
                'w-24', 'h-24', 'mx-auto', 'bg-blue-100', 'rounded-full',
                'flex', 'items-center', 'justify-center', 'shadow-sm'
              )}>
                <Smartphone className={clsx('w-12', 'h-12', 'text-blue-600')} />
              </div>
            </div>

            <button
              onClick={doLogin}
              className={clsx(
                'w-full', 'bg-blue-600', 'hover:bg-blue-700',
                'text-white', 'font-medium', 'py-4', 'px-6', 'rounded-xl',
                'flex', 'items-center', 'justify-center', 'space-x-3',
                'transition-colors', 'duration-200', 'shadow-sm'
              )}
            >
              <ExternalLink className={clsx('w-5', 'h-5')} />
              <span>前往钉钉登录</span>
            </button>

            {/* 登录状态信息 */}
            {timeLeft > 0 && (
              <div className={clsx(
                'flex', 'items-center', 'justify-center', 'space-x-2',
                'text-sm', 'text-gray-600'
              )}>
                <Clock className={clsx('w-4', 'h-4')} />
                <span>授权将在</span>
                <span className={clsx('font-mono', 'text-blue-600', 'font-medium')}>
                  {formatTime(timeLeft)}
                </span>
                <span>后过期</span>
              </div>
            )}
          </div>
        )
      }

      // 桌面端显示二维码模式
      return (
        <div className={clsx('space-y-4')}>
          <div className={clsx(
            'relative', 'mx-auto', 'w-full', 'max-w-xs',
            'bg-white', 'border', 'border-gray-200', 'rounded-xl',
            'shadow-sm', 'overflow-hidden'
          )}>
            <button
              onClick={doLogin}
              className={clsx(
                'block', 'w-full', 'h-80', 'relative',
                'hover:bg-gray-50', 'transition-colors', 'duration-200'
              )}
            >
              <img
                src={qrImage}
                alt="钉钉登录二维码"
                className={clsx('w-full', 'h-full', 'object-contain', 'p-4')}
              />
              <div className={clsx(
                'absolute', 'bottom-2', 'left-2', 'right-2',
                'bg-black', 'bg-opacity-70', 'text-white',
                'text-xs', 'py-1', 'px-2', 'rounded',
                'flex', 'items-center', 'justify-center', 'space-x-1'
              )}>
                <QrCode className={clsx('w-3', 'h-3')} />
                <span>点击在新窗口打开</span>
              </div>
            </button>
          </div>

          {/* 二维码状态信息 */}
          {timeLeft > 0 && (
            <div className={clsx('text-center', 'space-y-2')}>
              <div className={clsx(
                'flex', 'items-center', 'justify-center', 'space-x-2',
                'text-sm', 'text-gray-600'
              )}>
                <Clock className={clsx('w-4', 'h-4')} />
                <span>二维码将在</span>
                <span className={clsx('font-mono', 'text-blue-600', 'font-medium')}>
                  {formatTime(timeLeft)}
                </span>
                <span>后过期</span>
              </div>
            </div>
          )}
        </div>
      )
    }

    return null
  }

  return (
    <div className={clsx('p-8', 'space-y-6')}>
      {/* 标题区域 */}
      <div className={clsx('text-center', 'space-y-2')}>
        <h3 className={clsx('text-xl', 'font-semibold', 'text-gray-800')}>
          {isMobile ? '钉钉登录' : '扫码登录'}
        </h3>
        <p className={clsx('text-sm', 'text-gray-600')}>
          {isMobile 
            ? '请点击下方按钮跳转到钉钉客户端完成登录'
            : '请使用钉钉客户端扫描下方二维码完成登录'
          }
        </p>
      </div>

      {/* 二维码区域 */}
      {renderQRCodeArea()}
    </div>
  )
}

export default QRCodeLogin
